import { Field, type HTMLArkProps } from '@ark-ui/react';

import type { Scale3 } from '../../lib/types';
import { cn } from '../../lib/utils';

type TextAreaRootProps = Omit<Field.TextareaProps, 'size'> & {
	size?: Scale3;
	variant?: 'classic' | 'surface' | 'soft' | 'outline';
	disabled?: boolean;
	invalid?: boolean;
	required?: boolean;
	readOnly?: boolean;
	resize?: 'none' | 'vertical' | 'horizontal' | 'both';
	rootClassName?: string;
	inputClassName?: string;
};

function TextAreaRoot({
	className,
	rootClassName,
	inputClassName,
	size = '2',
	variant = 'surface',
	disabled,
	invalid,
	required,
	readOnly,
	resize = 'vertical',
	children,
	...inputProps
}: TextAreaRootProps) {
	const hasCustomChildren = children != null;

	return (
		<Field.Root
			disabled={disabled}
			invalid={invalid}
			required={required}
			readOnly={readOnly}
			className={cn('pt-text-area', rootClassName, !hasCustomChildren && className)}
			data-size={size}
			data-variant={variant}
			data-resize={resize}
		>
			{hasCustomChildren ? (
				children
			) : (
				<Field.Textarea
					className={cn('pt-text-area-input', className, inputClassName)}
					{...inputProps}
				/>
			)}
		</Field.Root>
	);
}

function TextAreaLabel({ className, ...props }: HTMLArkProps<'label'>) {
	return <Field.Label className={cn('pt-text-area-label', className)} {...props} />;
}

function TextAreaInput({ className, ...props }: HTMLArkProps<'textarea'>) {
	return <Field.Textarea className={cn('pt-text-area-input', className)} {...props} />;
}

function TextAreaHelper({ className, ...props }: HTMLArkProps<'span'>) {
	return <Field.HelperText className={cn('pt-text-area-helper', className)} {...props} />;
}

function TextAreaError({ className, ...props }: HTMLArkProps<'span'>) {
	return <Field.ErrorText className={cn('pt-text-area-error', className)} {...props} />;
}

const TextArea = Object.assign(TextAreaRoot, {
	Root: TextAreaRoot,
	Label: TextAreaLabel,
	Input: TextAreaInput,
	Helper: TextAreaHelper,
	Error: TextAreaError
});

export { TextArea, type TextAreaRootProps as TextAreaProps };
